import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import {
  getSafetyConfig,
  getSafetyResultSummary,
  getSafetyProjectDetail,
  getSafetyBasicInfo,
  saveSafetyProject
} from '../../../api/safetyAssessApi';
import SafetyApprovalLineModal from '../../../components/OrgSelect/SafetyApprovalLineModal';
import '../Safety.css';
import SafetyPreparationTab from './steps/SafetyPreparationTab';
import SafetyHazardIdentify from './steps/SafetyHazardIdentify';
import SafetyProjectItemGrid from './steps/SafetyProjectItemGrid';
import SafetyMeetingTrainingForm from './steps/SafetyMeetingTrainingForm';

function SafetyProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState('STEP_EVAL');
  const [hoveredTab, setHoveredTab] = useState(null);
  const [projectInfo, setProjectInfo] = useState({
    id: id || 1,
    name: '', siteName: '', period: '', workerCnt: 0,
    drafter: '', reviewer: '', approver: '',
    eduCertUrl: null, patrolImgUrl: null
  });



  // 평가 기법 및 회의록 상태
  const [evalMethod, setEvalMethod] = useState('3x3');
  const [gradeRules, setGradeRules] = useState([]);
  const [summaryData, setSummaryData] = useState({
    id: null, conclusion: '', meetingDate: '', location: '', content: '', instructor: '', attendeeCnt: '', completeCnt: '', attendees: ''
  });

  // eslint-disable-next-line no-unused-vars
  const [basicInfo, setBasicInfo] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalModalInitialLines, setApprovalModalInitialLines] = useState(null);

  // 헤더 영역 전체 숨기기/보이기 상태
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  // 데이터 로드
  const loadAllData = useCallback(async () => {
    if (!token || !id) return;
    try {
      const projDetail = await getSafetyProjectDetail(id, token);
      if (projDetail) {
        setProjectInfo(projDetail);
        if (projDetail.status === 'completed') {
          setActiveTab('STEP_REPORT');
        } else {
          setActiveTab('STEP_EVAL');
        }
      }

      const configData = await getSafetyConfig(token);
      if (configData) {
        setEvalMethod(configData.evalMethod || '3x3');
        setGradeRules(configData.gradeRules || []);
      }

      const bInfo = await getSafetyBasicInfo(token);
      if (bInfo) setBasicInfo(bInfo);

      const summary = await getSafetyResultSummary(id, token);
      if (summary && summary.id) {
        setSummaryData({
          id: summary.id,
          conclusion: summary.conclusion || '',
          meetingDate: summary.meetingDate ? summary.meetingDate.split('T')[0] : '',
          location: summary.location || '',
          content: summary.content || '',
          instructor: summary.instructor || '',
          attendeeCnt: summary.attendeeCnt || '',
          completeCnt: summary.completeCnt || '',
          attendees: summary.attendees || ''
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [id, token]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const syncProjectInfo = async () => {
    try {
      const projDetail = await getSafetyProjectDetail(id, token);
      if (projDetail) setProjectInfo(projDetail);
    } catch (err) {
      console.error(err);
    }
  };



  // 결재선 저장
  const handleSaveProjectInfo = async (updatedInfo) => {
    try {
      const payload = {
        ...projectInfo,
        ...updatedInfo,
        id
      };
      
      // [치명적 버그 수정] API 호출로 실제 DB에 결재선 업데이트를 날려야 함!
      await saveSafetyProject(payload, token);

      // 백엔드에 저장 후 다시 불러오지만, 백엔드 스키마에 없는 id/position 필드는 프론트에서 유지해준다.
      const preservedFields = {
        drafter_id: projectInfo.drafter_id || updatedInfo.drafter_id,
        reviewer_id: projectInfo.reviewer_id || updatedInfo.reviewer_id,
        reviewer_position: projectInfo.reviewer_position || updatedInfo.reviewer_position,
        approver_id: projectInfo.approver_id || updatedInfo.approver_id,
        approver_position: projectInfo.approver_position || updatedInfo.approver_position
      };
      await syncProjectInfo();
      setProjectInfo(prev => ({ ...prev, ...preservedFields }));
    } catch (err) {
      console.error(err);
      alert('결재선 저장 실패');
    }
  };

  const getGradeInfo = (score) => {
    const rule = gradeRules.find(r => score >= r.min && score <= r.max);
    return rule ? { grade: rule.grade, color: rule.color } : { grade: '미정', color: '#94a3b8' };
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const currentUserId = user?.userid || user?.userId;

  // 권한 자격 유형 정밀 판별
  const getUserRoleType = () => {
    if (user?.role === 'admin') return 'ADMIN';
    if (currentUserId && projectInfo.writeId && currentUserId.toString() === projectInfo.writeId.toString()) return 'PROJECT_WRITER';
    if (currentUserId && projectInfo.managerId && currentUserId.toString() === projectInfo.managerId.toString()) return 'PROJECT_MANAGER';
    if (basicInfo?.managerId && currentUserId && currentUserId.toString() === basicInfo.managerId.toString()) return 'BASIC_MANAGER';
    return 'GUEST';
  };

  const userRoleType = getUserRoleType();
  const hasPermission = userRoleType !== 'GUEST';

  return (
    <div className="sf-page sf-animate">
      
      {!isHeaderVisible && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '16px',
          background: '#ffffff',
          padding: '12px 20px',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)'
        }}>
          {/* 미니 스텝퍼 (설명 생략 버전) */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { key: 'STEP_PREP', num: '01', label: '사전준비' },
              { key: 'STEP_HAZARD', num: '02', label: '요인 파악' },
              { key: 'STEP_EVAL', num: '03', label: '평가 / 조치' },
              { key: 'STEP_REPORT', num: '04', label: '결재 / 일지' }
            ].map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <div 
                  key={tab.key} 
                  onClick={() => handleTabChange(tab.key)} 
                  style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    padding: '8px 16px', 
                    borderRadius: '10px', 
                    cursor: 'pointer',
                    background: isActive ? 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)' : '#f8fafc',
                    border: isActive ? '1px solid transparent' : '1px solid #e2e8f0',
                    color: isActive ? '#ffffff' : '#64748b',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 56, 202, 0.2)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{
                    background: isActive ? '#38bdf8' : '#e2e8f0',
                    color: isActive ? '#0f172a' : '#64748b',
                    padding: '4px 8px', 
                    borderRadius: '6px', 
                    fontSize: '0.8rem', 
                    fontWeight: 900
                  }}>
                    {tab.num}
                  </span>
                  <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>{tab.label}</span>
                </div>
              );
            })}
          </div>

          <button 
            onClick={() => setIsHeaderVisible(true)}
            style={{ 
              background: '#e2e8f0', 
              border: 'none', 
              color: '#475569', 
              fontSize: '0.8rem', 
              fontWeight: 600, 
              padding: '0 16px', 
              borderRadius: '6px', 
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s',
              height: '32px',
              whiteSpace: 'nowrap',
              marginLeft: '16px'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#cbd5e1'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
          >
            정보 펼치기 ▼
          </button>
        </div>
      )}

      {/* 대기업형 프리미엄 인디케이터 헤더 배너 (Enterprise Light Slate Panel - Compact) */}
      {isHeaderVisible && (
        <div style={{
        padding: '16px 24px 12px 24px',
        background: 'linear-gradient(to right, #f8fafc 0%, #ffffff 100%)',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)',
        marginBottom: '16px',
        color: '#1e293b'
      }}>
        {/* 상단: 타이틀, 메타정보 및 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h2 style={{ color: '#0f172a', fontWeight: 800, fontSize: '1.35rem', margin: '0 0 4px 0', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center' }}>
              <i className="fas fa-shield-alt text-primary me-2" style={{ fontSize: '1.15rem' }}></i>
              {projectInfo.name}
            </h2>
            <div style={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center' }}>
              <span>사업장: <strong style={{ color: '#1e293b' }}>{projectInfo.siteName}</strong></span>
              <span style={{ color: '#cbd5e1', margin: '0 8px' }}>|</span>
              <span>기간: <strong style={{ color: '#1e293b' }}>{projectInfo.period}</strong></span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="sf-btn" onClick={() => navigate('/safety/projects')} style={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#475569', fontSize: '0.82rem', fontWeight: 700, padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <i className="bi bi-list"></i> 목록으로
            </button>
            <button className="sf-btn" onClick={() => navigate('/safety/results')} style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)', border: 'none', color: '#ffffff', fontSize: '0.82rem', fontWeight: 700, padding: '8px 18px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)', display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <i className="bi bi-printer-fill"></i> 출력 센터 이동
            </button>
          </div>
        </div>

        {/* 하단: 임베디드 3열 세그먼트 알약 탭 (Embedded Hero Tabs) + 정보 접기 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '12px' }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '960px',
            gap: '12px'
          }}>
          {[
            { key: 'STEP_PREP', num: '01', label: '사전준비', desc: '기타 안전보건 정보', icon: 'bi bi-shield-check' },
            { key: 'STEP_HAZARD', num: '02', label: '요인 파악', desc: '유해위험요인 파악', icon: 'bi bi-search' },
            { key: 'STEP_EVAL', num: '03', label: '평가 / 조치', desc: '위험성 평가 및 대책', icon: 'bi bi-lightning-charge' },
            { key: 'STEP_REPORT', num: '04', label: '결재 / 일지', desc: '회의록 및 교육일지', icon: 'bi bi-journal-check' }
          ].map((tab) => {
            const isActive = activeTab === tab.key;
            const isHovered = hoveredTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                onMouseEnter={() => setHoveredTab(tab.key)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  width: '210px',
                  flexShrink: 0,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: isActive
                    ? 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)'
                    : (isHovered ? '#e2e8f0' : '#f1f5f9'),
                  border: isActive
                    ? '1px solid transparent'
                    : '1px solid #cbd5e1',
                  color: isActive ? '#ffffff' : '#64748b',
                  boxShadow: isActive
                    ? '0 10px 25px -5px rgba(67, 56, 202, 0.4), 0 8px 10px -6px rgba(67, 56, 202, 0.1)'
                    : (isHovered ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none'),
                  transform: isActive ? 'translateY(-2px)' : (isHovered ? 'translateY(-1px)' : 'translateY(0)'),
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Active Tab Glow Effect */}
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 60%)',
                    transform: 'translate(-20px, -20px)',
                    pointerEvents: 'none'
                  }} />
                )}
                <span style={{
                  background: isActive ? '#38bdf8' : '#e2e8f0',
                  color: isActive ? '#0f172a' : '#64748b',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: 900,
                  letterSpacing: '0.05em',
                  boxShadow: isActive ? '0 0 15px rgba(56, 189, 248, 0.4)' : 'none',
                  transition: 'all 0.3s ease'
                }}>
                  {tab.num}
                </span>
                <div style={{ zIndex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    color: isActive ? '#ffffff' : (isHovered ? '#1e293b' : '#475569'),
                    lineHeight: 1.2,
                    transition: 'color 0.3s ease',
                    whiteSpace: 'nowrap'
                  }}>
                    {tab.label}
                  </div>
                  <div style={{
                    fontSize: '0.72rem',
                    color: isActive ? '#a5b4fc' : '#94a3b8',
                    marginTop: '2px',
                    fontWeight: 600,
                    transition: 'color 0.3s ease',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {tab.desc}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          
          <button 
            onClick={() => setIsHeaderVisible(false)} 
            style={{ 
              background: '#e2e8f0', 
              border: 'none', 
              color: '#475569', 
              fontSize: '0.8rem', 
              fontWeight: 600, 
              padding: '0 16px', 
              borderRadius: '6px', 
              cursor: 'pointer', 
              display: 'inline-flex', 
              alignItems: 'center', 
              marginLeft: '12px',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s',
              height: '32px'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#cbd5e1'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
          >
            정보 접기 ▲
          </button>
        </div>
      </div>
      )}



      {/* 탭 1: 사전준비 */}
      {activeTab === 'STEP_PREP' && (
        <SafetyPreparationTab 
          userRoleType={userRoleType} 
          hasPermission={hasPermission} 
        />
      )}

      {/* 탭 2: 요인 파악 조감도 */}
      {activeTab === 'STEP_HAZARD' && (
        <SafetyHazardIdentify
          evalMethod={evalMethod}
          gradeRules={gradeRules}
          getGradeInfo={getGradeInfo}
          userRoleType={userRoleType}
          hasPermission={hasPermission}
        />
      )}

      {/* 탭 2: 위험성평가 목록 및 개별 CRUD */}
      {activeTab === 'STEP_EVAL' && (
        <SafetyProjectItemGrid
          assessmentId={id}
          assessmentName={projectInfo?.name || ''}
          evalMethod={evalMethod}
          getGradeInfo={getGradeInfo}
          gradeRules={gradeRules}
          projectStatus={projectInfo.status}
          hasPermission={user?.role === 'admin' || currentUserId === projectInfo.writeId || currentUserId === projectInfo.managerId || (basicInfo?.managerId && currentUserId === basicInfo?.managerId)}
        />
      )}

      {/* 탭 3: 회의록 및 교육일지 */}
      {activeTab === 'STEP_REPORT' && (
        <SafetyMeetingTrainingForm
          assessmentId={id}
          summaryData={summaryData}
          setSummaryData={setSummaryData}
          projectInfo={projectInfo}
          syncProjectInfo={syncProjectInfo}
          projectStatus={projectInfo.status}
          onOpenApprovalModal={(initialLines) => {
            setApprovalModalInitialLines(initialLines || null);
            setShowApprovalModal(true);
          }}
        />
      )}

      {/* 결재선 지정 모달 */}
      {showApprovalModal && (
        <SafetyApprovalLineModal
          writer={{ 
            userid: user?.userid, 
            name: user?.UserName || user?.name, 
            position: user?.position || user?.rank,
            dept_name: user?.department || user?.dept_name
          }}
          initialSelected={approvalModalInitialLines || {
            drafter: projectInfo.drafter && projectInfo.drafter !== '미지정' ? { name: projectInfo.drafter } : null,
            reviewer: projectInfo.reviewer && projectInfo.reviewer !== '미지정' ? { name: projectInfo.reviewer } : null,
            approver: projectInfo.approver && projectInfo.approver !== '미지정' ? { name: projectInfo.approver } : null
          }}
          onConfirm={(roles) => {
            const updated = {
              drafter: roles.drafter?.name || '',
              drafter_id: roles.drafter?.userid || roles.drafter?.user_id || '',
              drafter_position: roles.drafter?.user_position || roles.drafter?.position || roles.drafter?.rank || '',
              reviewer: roles.reviewer?.name || '',
              reviewer_id: roles.reviewer?.userid || roles.reviewer?.user_id || '',
              reviewer_position: roles.reviewer?.user_position || roles.reviewer?.position || roles.reviewer?.rank || '',
              approver: roles.approver?.name || '',
              approver_id: roles.approver?.userid || roles.approver?.user_id || '',
              approver_position: roles.approver?.user_position || roles.approver?.position || roles.approver?.rank || ''
            };
            setProjectInfo(prev => ({
              ...prev,
              ...updated
            }));
            handleSaveProjectInfo(updated);
            setShowApprovalModal(false);
          }}
          onCancel={() => setShowApprovalModal(false)}
        />
      )}
    </div>
  );
}

export default SafetyProjectDetail;
